import { useState } from 'react';
import { TASK_STATUS, TASK_STATUS_LABELS, type Task, type TaskInput, type TaskStatus } from '@stromo/shared';
import { InlineDateCell, InlineNumberCell, InlineTextCell } from './InlineCell';
import { diffDays, isTaskDelayed } from '../lib/delay';
import { useUsersLookup } from '../lib/lookups';
import { useAuthStore } from '../lib/authStore';

interface TaskTableProps {
  tasks: Task[];
  onUpdate: (id: number, patch: Partial<TaskInput>) => void;
  onDelete?: (id: number) => void;
  /** Wenn gesetzt: Drag-and-Drop aktiv, Callback mit neuer ID-Reihenfolge bei Drop. */
  onReorder?: (orderedIds: number[]) => void;
  /** Wenn gesetzt: "+ Aufgabe"-Knopf neben jedem Gruppen-Header. */
  onAddTaskAfter?: (groupTaskId: number) => void;
  /** Read-Only Mode für Rollen ohne Schreibrechte (Mitarbeiter). Alle
   *  Edit-Steuerelemente werden deaktiviert/ausgeblendet. */
  readOnly?: boolean;
  highlightTaskId?: number | null;
}

export function TaskTable({
  tasks,
  onUpdate,
  onDelete,
  onReorder,
  onAddTaskAfter,
  readOnly = false,
  highlightTaskId,
}: TaskTableProps) {
  const users = useUsersLookup();
  const { user } = useAuthStore();
  const canDelete = !readOnly && (user?.role === 'admin' || user?.role === 'project_manager');
  // In readOnly werden Drag/+Aufgabe-Knopf ausgeschaltet, indem wir die
  // Callbacks lokal als undefined behandeln.
  const effectiveOnReorder = readOnly ? undefined : onReorder;
  const effectiveOnAddTaskAfter = readOnly ? undefined : onAddTaskAfter;
  const effectiveOnDelete = readOnly ? undefined : onDelete;
  // InlineCell/Select-Helper, der bei readOnly den Update-Aufruf einfach
  // schluckt — die Cells sind ohnehin disabled, das ist zusätzliche
  // Verteidigungslinie für selects, deren onChange auch im disabled-Mode
  // nicht feuern sollte.
  const safeUpdate: typeof onUpdate = readOnly ? () => undefined : onUpdate;
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const handleDrop = (targetIdx: number) => {
    if (dragIdx == null || dragIdx === targetIdx) {
      setDragIdx(null);
      setDragOverIdx(null);
      return;
    }
    const next = [...tasks];
    const [moved] = next.splice(dragIdx, 1);
    if (!moved) {
      setDragIdx(null);
      setDragOverIdx(null);
      return;
    }
    next.splice(targetIdx, 0, moved);
    setDragIdx(null);
    setDragOverIdx(null);
    effectiveOnReorder?.(next.map((t) => t.id));
  };

  return (
    <div className="task-table-wrap">
      <table className="task-tbl">
        <thead>
          <tr>
            {effectiveOnReorder && <th style={{ width: 24 }} aria-label="Reihenfolge" />}
            <th>Pos.</th>
            <th>Arbeitsphase / Tätigkeit</th>
            <th>Bauzeit von</th>
            <th>Bauzeit bis</th>
            <th>Unser von</th>
            <th>Unser bis</th>
            <th>Diff.</th>
            <th>Status</th>
            <th>%</th>
            <th>Bauleiter</th>
            <th>Subunternehmer</th>
            <th>Bemerkungen</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {tasks.length === 0 && (
            <tr>
              <td colSpan={effectiveOnReorder ? 14 : 13} style={{ padding: 30, textAlign: 'center', color: 'var(--color-text-soft)' }}>
                Keine Aufgaben in dieser Sektion.
              </td>
            </tr>
          )}
          {tasks.map((t, idx) => {
            const isHighlighted = highlightTaskId === t.id;
            const rowDragProps = effectiveOnReorder
              ? {
                  draggable: true,
                  onDragStart: () => setDragIdx(idx),
                  onDragOver: (e: React.DragEvent) => {
                    e.preventDefault();
                    if (dragOverIdx !== idx) setDragOverIdx(idx);
                  },
                  onDrop: () => handleDrop(idx),
                  onDragEnd: () => {
                    setDragIdx(null);
                    setDragOverIdx(null);
                  },
                }
              : {};
            const dragOverCls = dragOverIdx === idx && dragIdx !== idx ? ' is-drop-target' : '';
            const draggingCls = dragIdx === idx ? ' is-dragging' : '';

            if (t.is_group_header) {
              return (
                <tr
                  key={t.id}
                  id={`task-row-${t.id}`}
                  className={`group-header${dragOverCls}${draggingCls}`}
                  {...rowDragProps}
                >
                  {effectiveOnReorder && (
                    <td className="drag-handle" aria-hidden="true" title="Zum Verschieben ziehen">⋮⋮</td>
                  )}
                  <td className="pos">{t.position_number || ''}</td>
                  <td colSpan={11}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <InlineTextCell value={t.title} onCommit={(v) => v && safeUpdate(t.id, { title: v })} disabled={readOnly} />
                      </div>
                      {effectiveOnAddTaskAfter && (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => effectiveOnAddTaskAfter(t.id)}
                          title="Aufgabe direkt unter dieser Gruppe einfügen"
                          style={{ flexShrink: 0, textTransform: 'none', letterSpacing: 0 }}
                        >
                          + Aufgabe
                        </button>
                      )}
                    </div>
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {canDelete && effectiveOnDelete && (
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => effectiveOnDelete(t.id)} title="Gruppe entfernen">
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              );
            }

            const delayed = isTaskDelayed(t);
            const delta = diffDays(t.schedule_end, t.internal_end);

            return (
              <tr
                key={t.id}
                id={`task-row-${t.id}`}
                className={[delayed ? 'delayed' : '', isHighlighted ? 'task-highlight' : '', dragOverCls.trim(), draggingCls.trim()].filter(Boolean).join(' ') || undefined}
                {...rowDragProps}
              >
                {onReorder && (
                  <td className="drag-handle" aria-hidden="true" title="Zum Verschieben ziehen">⋮⋮</td>
                )}
                <td className="pos">{t.position_number}</td>
                <td>
                  <InlineTextCell value={t.title} onCommit={(v) => v && safeUpdate(t.id, { title: v })} disabled={readOnly} />
                </td>
                <td>
                  <InlineDateCell value={t.schedule_start} onCommit={(v) => safeUpdate(t.id, { schedule_start: v })} disabled={readOnly} />
                </td>
                <td>
                  <InlineDateCell value={t.schedule_end} onCommit={(v) => safeUpdate(t.id, { schedule_end: v })} disabled={readOnly} />
                </td>
                <td>
                  <InlineDateCell value={t.internal_start} onCommit={(v) => safeUpdate(t.id, { internal_start: v })} disabled={readOnly} />
                </td>
                <td>
                  <InlineDateCell value={t.internal_end} onCommit={(v) => safeUpdate(t.id, { internal_end: v })} disabled={readOnly} />
                </td>
                <td style={{
                  fontFamily: 'monospace',
                  color: delta == null ? 'var(--color-text-soft)' : delta > 0 ? 'var(--color-danger)' : delta < 0 ? 'var(--color-success)' : 'var(--color-text-strong)',
                  fontWeight: delta != null && delta !== 0 ? 600 : 400,
                }}>
                  {delta == null ? '—' : delta > 0 ? `+${delta}` : delta}
                </td>
                <td>
                  <select
                    value={t.status}
                    data-status={t.status}
                    onChange={(e) => safeUpdate(t.id, { status: e.target.value as TaskStatus })}
                    disabled={readOnly}
                    style={{ minWidth: 130 }}
                  >
                    {TASK_STATUS.map((s) => (
                      <option key={s} value={s}>{TASK_STATUS_LABELS[s]}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <InlineNumberCell
                    value={t.progress_percent}
                    min={0}
                    max={100}
                    suffix="%"
                    onCommit={(v) => safeUpdate(t.id, { progress_percent: v ?? 0 })}
                    disabled={readOnly}
                  />
                </td>
                <td>
                  <select
                    value={t.site_manager_id ?? ''}
                    onChange={(e) => safeUpdate(t.id, { site_manager_id: e.target.value ? Number(e.target.value) : null })}
                    disabled={readOnly}
                    style={{ padding: '3px 6px', fontSize: 12, minWidth: 100 }}
                  >
                    <option value="">—</option>
                    {users.data?.map((u) => (
                      <option key={u.id} value={u.id}>{u.abbreviation}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <InlineTextCell value={t.subcontractor} onCommit={(v) => safeUpdate(t.id, { subcontractor: v })} disabled={readOnly} />
                </td>
                <td style={{ maxWidth: 220 }}>
                  <InlineTextCell value={t.notes} onCommit={(v) => safeUpdate(t.id, { notes: v })} multiline disabled={readOnly} />
                </td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                  {canDelete && effectiveOnDelete && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => effectiveOnDelete(t.id)}
                      title="Aufgabe entfernen"
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
